import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../store/hooks';
import { LANGUAGES } from '../../utils/consts';
import { useLocation } from 'react-router-dom';
import enTranslations from './en.json';
import plTranslations from './pl.json';

type Translations = Record<string, unknown>;

const TRANSLATION_DICTIONARY: Partial<Record<LANGUAGES, Translations>> = {
    [LANGUAGES.EN]: enTranslations,
    [LANGUAGES.PL]: plTranslations,
};

const TranslationContext = createContext<{
    t: (key: string) => string;
    isLoaded: boolean;
}>({
    t: key => key,
    isLoaded: true,
});

function getNestedValue(obj: any, path: string): string {
    return (
        path.split('.').reduce((acc, key) => acc?.[key], obj) ??
        `Invalid property: ${path}`
    );
}

export const TranslationProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const auth = useAppSelector(state => state.auth);
    const location = useLocation();

    const pathLang = useMemo(() => {
        const firstSegment = location.pathname
            .split('/')
            .filter(Boolean)[0]
            ?.toUpperCase();

        if (firstSegment === LANGUAGES.PL) {
            return LANGUAGES.PL;
        }

        if (firstSegment === LANGUAGES.EN) {
            return LANGUAGES.EN;
        }
        return null;
    }, [location.pathname]);

    const lang = useMemo(() => {
        if (pathLang) {
            return pathLang;
        }

        return (
            auth.authData?.language ||
            (navigator.language.split('-')[0].toUpperCase() in LANGUAGES
                ? (navigator.language.split('-')[0].toUpperCase() as LANGUAGES)
                : LANGUAGES.EN)
        );
    }, [auth.authData, pathLang]);

    useEffect(() => {
        document.documentElement.lang = lang.toLowerCase();
    }, [lang]);

    const translations =
        TRANSLATION_DICTIONARY[lang] || TRANSLATION_DICTIONARY[LANGUAGES.EN];

    const t = (key: string) => getNestedValue(translations, key);

    return (
        <TranslationContext.Provider value={{ t, isLoaded: true }}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => useContext(TranslationContext);
